/**
 * Combo of 3: three units from three different categories → bundle price (default ₹2,999).
 * Applies the bundle before cart UI renders so customers never see a 3-item → combo flash.
 */
(function () {
  const CATEGORY_GROUPS = [
    { key: 'chains', handles: ['chains', 'signature-chains'] },
    { key: 'bracelets', handles: ['bracelets'] },
    { key: 'rings', handles: ['rings', 'wrist-essentials'] },
    { key: 'earrings', handles: ['earrings'] },
    { key: 'pendents', handles: ['pendents', 'pendants'] },
  ];

  const CATEGORY_LABELS = {
    chains: 'Chains',
    bracelets: 'Bracelets',
    rings: 'Rings',
    earrings: 'Earrings',
    pendents: 'Pendants',
  };

  let bundleOperationInFlight = false;
  let bundleSyncPromise = null;
  let comboCartCache = null;

  function resolveCategory(collectionHandles) {
    if (!collectionHandles || !collectionHandles.length) return null;

    for (const group of CATEGORY_GROUPS) {
      if (group.handles.some((handle) => collectionHandles.includes(handle))) {
        return group.key;
      }
    }
    return null;
  }

  function isBundleLine(item, bundleVariantId) {
    return bundleVariantId && String(item.variantId ?? item.variant_id) === String(bundleVariantId);
  }

  function countBundles(cart, bundleVariantId) {
    if (!cart?.items || !bundleVariantId) return 0;
    return cart.items
      .filter((item) => String(item.variant_id) === String(bundleVariantId))
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  function countBundlesFromConfig(items, bundleVariantId) {
    if (!bundleVariantId || !items?.length) return 0;
    return items.reduce((sum, item) => {
      if (String(item.variantId) === String(bundleVariantId)) {
        return sum + (Number(item.quantity) || 0);
      }
      return sum;
    }, 0);
  }

  function evaluateCombo(items, bundleVariantId) {
    const buckets = {};

    for (const item of items) {
      if (isBundleLine(item, bundleVariantId)) continue;

      const quantity = Number(item.quantity);
      if (!Number.isFinite(quantity) || quantity < 1) continue;

      const category = resolveCategory(item.collections);
      if (!category) continue;

      const unitPrice = Number(item.unitPrice);
      if (!Number.isFinite(unitPrice)) continue;

      if (!buckets[category] || unitPrice > buckets[category].unitPrice) {
        buckets[category] = {
          category,
          unitPrice,
          formattedUnitPrice: item.formattedUnitPrice,
          title: item.title,
          key: item.key,
          variantId: item.variantId,
          quantity,
          imageUrl: item.imageUrl,
          imageAlt: item.imageAlt,
        };
      }
    }

    const categories = Object.keys(buckets);
    if (categories.length < 3) return null;

    const entries = categories
      .sort((a, b) => buckets[b].unitPrice - buckets[a].unitPrice)
      .slice(0, 3)
      .map((key) => buckets[key]);

    const subtotal = entries.reduce((sum, entry) => sum + entry.unitPrice, 0);

    return { entries, subtotal };
  }

  function getComboProgress(items, bundleVariantId) {
    const buckets = {};
    let hasItems = false;

    for (const item of items) {
      if (isBundleLine(item, bundleVariantId)) continue;

      const quantity = Number(item.quantity);
      if (!Number.isFinite(quantity) || quantity < 1) continue;

      hasItems = true;
      const category = resolveCategory(item.collections);
      if (!category) continue;

      const unitPrice = Number(item.unitPrice);
      if (!buckets[category] || unitPrice > buckets[category].unitPrice) {
        buckets[category] = { category, title: item.title };
      }
    }

    const categories = Object.keys(buckets).map((key) => ({
      category: key,
      label: CATEGORY_LABELS[key] || key,
      title: buckets[key].title,
    }));

    return {
      hasItems,
      categoryCount: categories.length,
      categories,
      needed: Math.max(0, 3 - categories.length),
    };
  }

  function formatPriceLabel(config) {
    return config.comboPriceFormatted || '₹2,999';
  }

  function setBannerMode(banner, mode) {
    banner.classList.remove(
      'cart-combo-banner--unlock',
      'cart-combo-banner--ready',
      'cart-combo-banner--active'
    );
    if (mode) banner.classList.add(`cart-combo-banner--${mode}`);
  }

  function updateProgressBar(banner, categoryCount) {
    const progressEl = banner.querySelector('[data-cart-combo-progress]');
    if (!progressEl) return;

    const steps = progressEl.querySelectorAll('.cart-combo-banner__progress-step');
    const show = categoryCount > 0 && categoryCount < 3;

    progressEl.hidden = !show;
    progressEl.setAttribute('aria-hidden', show ? 'false' : 'true');

    steps.forEach((step, index) => {
      step.classList.toggle('is-filled', index < categoryCount);
    });
  }

  function renderBanner(root, combo, progress, strings, config, bundleCount, applyError) {
    const banner = root.querySelector('[data-cart-combo-banner]');
    const titleEl = root.querySelector('[data-cart-combo-title]');
    const messageEl = root.querySelector('[data-cart-combo-message]');
    const listEl = root.querySelector('[data-cart-combo-list]');
    const priceEl = root.querySelector('[data-cart-combo-price]');
    const errorEl = root.querySelector('[data-cart-combo-error]');

    if (!banner || !titleEl || !messageEl || !listEl) return;

    const bundleActive = bundleCount > 0;
    const priceLabel = formatPriceLabel(config);
    const offerEnabled = Boolean(config.bundleVariantId);

    const showUnlock =
      offerEnabled && progress.hasItems && !combo && !bundleActive && progress.needed > 0;

    if (!combo && !bundleActive && !showUnlock) {
      banner.hidden = true;
      banner.setAttribute('aria-hidden', 'true');
      listEl.innerHTML = '';
      if (priceEl) priceEl.textContent = '';
      if (errorEl) errorEl.hidden = true;
      setBannerMode(banner, null);
      updateProgressBar(banner, 0);
      return;
    }

    banner.hidden = false;
    banner.setAttribute('aria-hidden', 'false');

    if (priceEl) {
      priceEl.textContent = priceLabel;
    }

    if (bundleActive && !combo) {
      setBannerMode(banner, 'active');
      titleEl.textContent = strings.activeTitle;
      const template =
        bundleCount > 1 ? strings.bundlesActiveMessage : strings.bundleActiveMessage;
      messageEl.textContent = template
        .replace('[price]', priceLabel)
        .replace('[count]', String(bundleCount));
      listEl.innerHTML = '';
      updateProgressBar(banner, 3);
    } else if (bundleActive && combo) {
      setBannerMode(banner, 'ready');
      titleEl.textContent = strings.readyTitle || strings.activeTitle;
      messageEl.textContent = strings.bundleAndPendingMessage
        .replace('[price]', priceLabel)
        .replace('[count]', String(bundleCount));
      listEl.innerHTML = (combo.entries || [])
        .map((entry) => {
          const label = CATEGORY_LABELS[entry.category] || entry.category;
          return `<li><strong>${label}</strong> ${entry.title}</li>`;
        })
        .join('');
      updateProgressBar(banner, 3);
    } else if (combo) {
      setBannerMode(banner, 'ready');
      titleEl.textContent = strings.readyTitle || strings.activeTitle;
      messageEl.textContent = (strings.activeMessage || '').replace('[price]', priceLabel);
      listEl.innerHTML = (combo.entries || [])
        .map((entry) => {
          const label = CATEGORY_LABELS[entry.category] || entry.category;
          return `<li><strong>${label}</strong> ${entry.title}</li>`;
        })
        .join('');
      updateProgressBar(banner, 3);
    } else if (showUnlock) {
      setBannerMode(banner, 'unlock');
      titleEl.textContent = strings.unlockTitle || 'Build your Combo of 3';

      let message = (strings.unlockMessage || '').replace('[price]', priceLabel);

      if (progress.categoryCount === 0) {
        message = `${message} ${strings.unlockNoCategories || ''}`.trim();
        listEl.innerHTML = '';
      } else {
        if (progress.needed === 1) {
          message = `${message} ${strings.unlockNeedOne || ''}`.trim();
        } else if (progress.needed === 2) {
          message = `${message} ${strings.unlockNeedTwo || ''}`.trim();
        }
        if (strings.unlockCategoriesHint) {
          message = `${message} ${strings.unlockCategoriesHint}`.trim();
        }
        const chips = progress.categories
          .map((c) => `<li><strong>${c.label}</strong> ${c.title}</li>`)
          .join('');
        listEl.innerHTML = chips
          ? `<li class="cart-combo-banner__list-label">${strings.unlockInCart || 'In your cart'}</li>${chips}`
          : '';
      }

      messageEl.textContent = message;
      updateProgressBar(banner, progress.categoryCount);
    }

    if (errorEl) {
      if (applyError) {
        errorEl.textContent = applyError;
        errorEl.hidden = false;
      } else {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
    }
  }

  function cartApiUrl(route) {
    const base = typeof routes !== 'undefined' ? routes[route] : null;
    if (!base) return null;
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  async function fetchCart() {
    const url = cartApiUrl('cart_url') || '/cart.js';
    const response = await fetch(url);
    const cart = await response.json();
    comboCartCache = cart;
    return cart;
  }

  function setComboCartCache(cart) {
    if (cart?.items) comboCartCache = cart;
  }

  async function postJson(url, body) {
    if (!url) throw new Error('Cart API URL is not available');
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  }

  function cartAddFailed(result) {
    return Boolean(result && result.status);
  }

  function itemHandle(item) {
    if (item.handle) return item.handle;
    const match = String(item.url || '').match(/\/products\/([^/?]+)/);
    return match ? match[1] : null;
  }

  function cartItemToComboItem(item) {
    const handle = itemHandle(item);
    const category = handle ? window.__comboCategoryByHandle?.[handle] : null;
    const group = category ? CATEGORY_GROUPS.find((entry) => entry.key === category) : null;
    const imageUrl =
      typeof item.image === 'string'
        ? item.image
        : item.imageUrl || item.featured_image?.url || '';

    return {
      key: item.key,
      variantId: item.variant_id ?? item.variantId,
      unitPrice: item.final_price ?? item.unitPrice ?? item.price,
      formattedUnitPrice: item.final_price ?? item.unitPrice ?? item.price,
      quantity: item.quantity,
      title: item.product_title || item.title,
      imageUrl,
      imageAlt: item.featured_image?.alt || item.imageAlt || item.product_title || item.title || '',
      collections: group ? group.handles : item.collections || [],
    };
  }

  function cartFromConfig(config) {
    return {
      total_price: config.cartTotal,
      items: (config.items || []).map((item) => ({
        key: item.key,
        variant_id: item.variantId,
        quantity: item.quantity,
        product_title: item.title,
        title: item.title,
        final_price: item.unitPrice,
        price: item.unitPrice,
        image: item.imageUrl,
        featured_image: item.imageUrl ? { url: item.imageUrl, alt: item.imageAlt } : null,
        properties: {},
      })),
      attributes: {},
    };
  }

  function buildImageLookupFromItems(items) {
    const lookup = {};
    for (const item of items || []) {
      if (item.title && item.imageUrl) {
        lookup[item.title] = { url: item.imageUrl, alt: item.imageAlt || item.title };
      }
    }
    return lookup;
  }

  async function fetchCartSections(sectionIds, path = window.location.pathname) {
    if (!sectionIds?.length) return {};
    const response = await fetch(`${path}?sections=${sectionIds.join(',')}`);
    return response.json();
  }

  function setCartComboSyncing(active) {
    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) cartItemsEl.classList.toggle('cart-combo-syncing', active);
  }

  async function applyComboBundle(combo, bundleVariantId) {
    const bundleId = String(bundleVariantId);
    const updates = {};
    const backup = [];

    for (const entry of combo.entries) {
      const remaining = Math.max(0, Number(entry.quantity) - 1);
      if (entry.key) {
        updates[entry.key] = remaining;
      } else {
        updates[entry.variantId] = remaining;
      }

      backup.push({
        id: entry.variantId,
        quantity: 1,
        properties: {},
        title: entry.title,
        image: entry.imageUrl || '',
      });
    }

    const addResult = await postJson(cartApiUrl('cart_add_url'), {
      items: [
        {
          id: bundleId,
          quantity: 1,
          properties: {
            'Piece 1': combo.entries[0].title,
            'Piece 2': combo.entries[1].title,
            'Piece 3': combo.entries[2].title,
            '_piece_1_image': combo.entries[0].imageUrl || '',
            '_piece_1_image_alt': combo.entries[0].imageAlt || '',
            '_piece_2_image': combo.entries[1].imageUrl || '',
            '_piece_2_image_alt': combo.entries[1].imageAlt || '',
            '_piece_3_image': combo.entries[2].imageUrl || '',
            '_piece_3_image_alt': combo.entries[2].imageAlt || '',
          },
        },
      ],
    });

    if (cartAddFailed(addResult)) {
      const reason = addResult.description || addResult.message || 'Unknown error';
      return {
        ok: false,
        error: `Combo product could not be added: ${reason}. Make sure the Combo of 3 product is active and available.`,
      };
    }

    const cart = await postJson(cartApiUrl('cart_update_url'), {
      updates,
      attributes: {
        combo_bundle_active: '1',
        combo_backup: JSON.stringify(backup),
      },
    });

    if (cartAddFailed(cart)) {
      return { ok: false, error: cart.description || cart.message || 'Could not finalize combo.' };
    }

    setComboCartCache(cart);
    return { ok: true, cart };
  }

  async function clearComboAttributes() {
    await postJson(cartApiUrl('cart_update_url'), {
      attributes: {
        combo_bundle_active: '',
        combo_backup: '',
      },
    });
  }

  function formatMoney(cents, config) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2);
    if (config.moneyFormat) {
      return config.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    return amount;
  }

  function updateEstimatedTotal(cart, combo, config, hasBundles) {
    const totalEl = document.querySelector('.totals__total-value');
    const footer = document.getElementById('main-cart-footer');
    let row = document.getElementById('cart-combo-discount-row');

    if (!combo && !hasBundles) {
      if (row) row.remove();
      return;
    }

    const comboPrice = Number(config.comboPrice) || 299900;
    const savings = combo ? Math.max(0, combo.subtotal - comboPrice) : 0;
    let displayTotal = cart.total_price;

    if (combo && !hasBundles) {
      displayTotal = cart.total_price - combo.subtotal + comboPrice;
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(displayTotal, config);
    }

    if (savings <= 0) {
      if (row) row.remove();
      return;
    }

    if (!row && footer) {
      row = document.createElement('div');
      row.id = 'cart-combo-discount-row';
      row.className = 'cart-combo-discount-row';
      const totals = footer.querySelector('.totals');
      if (totals) totals.before(row);
    }

    if (row) {
      row.innerHTML = `
        <p class="cart-combo-discount-row__label">${config.strings?.discountLabel || 'Combo of 3 savings'}</p>
        <p class="cart-combo-discount-row__value">−${formatMoney(savings, config)}</p>
      `;
    }
  }

  async function refreshCartSections() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (!mainCartItems) return false;

    const sectionKeys = [
      mainCartItems.dataset.id,
      mainCartFooter?.dataset.id,
      'cart-icon-bubble',
      'cart-live-region-text',
    ].filter(Boolean);

    const data = await fetchCartSections(sectionKeys);

    const replaceInner = (elementId, selector, sectionKey) => {
      const html = data[sectionKey];
      if (!html) return;
      const element = document.getElementById(elementId);
      if (!element) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector(selector);
      const target = element.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    };

    replaceInner('main-cart-items', '.js-contents', mainCartItems.dataset.id);
    if (mainCartFooter) {
      replaceInner('main-cart-footer', '.js-contents', mainCartFooter.dataset.id);
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
      const source = doc.querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', false);
    if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', false);

    return true;
  }

  function waitForBundleSync() {
    if (!bundleSyncPromise && !bundleOperationInFlight) {
      return Promise.resolve();
    }
    return bundleSyncPromise || Promise.resolve();
  }

  function setupCheckoutGuard() {
    const form = document.getElementById('cart');
    const checkoutButton = document.getElementById('checkout');
    if (!form || !checkoutButton) return;

    form.addEventListener('submit', async (event) => {
      if (event.submitter?.name !== 'checkout') return;
      if (!bundleSyncPromise && !bundleOperationInFlight) return;

      event.preventDefault();
      await waitForBundleSync();
      form.requestSubmit(checkoutButton);
    });
  }

  async function runBundleApply(combo, bundleVariantId) {
    bundleOperationInFlight = true;
    try {
      return await applyComboBundle(combo, bundleVariantId);
    } finally {
      bundleOperationInFlight = false;
    }
  }

  async function syncComboBundle(combo, config, options = {}) {
    const bundleVariantId = config.bundleVariantId;
    if (!bundleVariantId) return { applied: false };

    if (bundleSyncPromise) return bundleSyncPromise;

    bundleSyncPromise = (async () => {
      const onCartPage = Boolean(document.getElementById('cart-combo-root'));
      if (onCartPage && options.showSyncState) setCartComboSyncing(true);

      try {
        let cart = null;
        if (combo) {
          if (config.items?.length) {
            cart = cartFromConfig(config);
          } else if (comboCartCache?.items?.length) {
            cart = comboCartCache;
          } else {
            cart = await fetchCart();
          }

          if (countBundles(cart, bundleVariantId) > 0) {
            return { applied: false, cart, bundleCount: countBundles(cart, bundleVariantId) };
          }

          const result = await runBundleApply(combo, bundleVariantId);
          if (result.ok) {
            if (onCartPage) await refreshCartSections();
            return { applied: true, cart: result.cart };
          }

          return {
            applied: false,
            cart,
            bundleCount: 0,
            applyError: result.error,
          };
        }

        cart = comboCartCache || (await fetchCart());
        if (countBundles(cart, bundleVariantId) === 0 && cart.attributes?.combo_bundle_active === '1') {
          await clearComboAttributes();
        }
        return { applied: false, cart, bundleCount: countBundles(cart, bundleVariantId) };
      } finally {
        if (onCartPage) setCartComboSyncing(false);
      }
    })();

    try {
      return await bundleSyncPromise;
    } finally {
      bundleSyncPromise = null;
    }
  }

  window.applyComboBeforeCartRender = async function applyComboBeforeCartRender(addResponse, sectionIds) {
    const bundleVariantId = window.__comboBundleVariantId;
    if (!bundleVariantId || !window.__comboCategoryByHandle) return addResponse;

    const cart = await fetchCart();
    if (countBundles(cart, bundleVariantId) > 0) return addResponse;

    const items = (cart.items || []).map(cartItemToComboItem);
    const combo = evaluateCombo(items, bundleVariantId);
    if (!combo) return addResponse;

    const result = await runBundleApply(combo, bundleVariantId);
    if (!result.ok) return addResponse;

    if (sectionIds?.length) {
      const sections = await fetchCartSections(sectionIds);
      return {
        ...addResponse,
        sections,
        items: result.cart.items,
        item_count: result.cart.item_count,
      };
    }

    return addResponse;
  };

  window.initCartComboOfThree = async function initCartComboOfThree(options = {}) {
    const root = document.getElementById('cart-combo-root');
    if (!root) return;

    const configEl = document.getElementById('cart-combo-config');
    if (!configEl) return;

    let config = {};
    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch (e) {
      console.error('Cart combo config parse error', e);
      return;
    }

    const banner = root.querySelector('[data-cart-combo-banner]');
    const isPrerendered = banner?.dataset.cartComboPrerendered === 'true';

    const items = config.items || [];
    const combo = evaluateCombo(items, config.bundleVariantId);
    const progress = getComboProgress(items, config.bundleVariantId);
    const bundleCount = countBundlesFromConfig(items, config.bundleVariantId);

    if (!isPrerendered) {
      renderBanner(root, combo, progress, config.strings || {}, config, bundleCount, null);
    }

    if (!options.skipBundleSync && combo && bundleCount === 0) {
      const handleSyncResult = async (syncResult) => {
        if (syncResult.applied) {
          await window.initCartComboOfThree({ skipBundleSync: true });
          return;
        }
        if (syncResult.applyError) {
          renderBanner(
            root,
            combo,
            progress,
            config.strings || {},
            config,
            bundleCount,
            syncResult.applyError
          );
        }
      };

      const syncOptions = { showSyncState: isPrerendered };
      if (isPrerendered) {
        syncComboBundle(combo, config, syncOptions).then(handleSyncResult).catch(() => {});
      } else {
        await handleSyncResult(await syncComboBundle(combo, config, syncOptions));
      }
    } else if (!options.skipBundleSync && !combo && bundleCount === 0) {
      await syncComboBundle(null, config);
    }

    if (Number.isFinite(config.cartTotal) && (combo || bundleCount > 0)) {
      updateEstimatedTotal({ total_price: config.cartTotal }, combo, config, bundleCount > 0);
    }

    if (banner) banner.dataset.needsBundleSync = 'false';
  };

  function bootCartCombo() {
    if (window.__comboBundleVariantId) {
      fetchCart().catch(() => {});
    }

    if (document.getElementById('cart-combo-root')) {
      setupCheckoutGuard();
      window.initCartComboOfThree();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootCartCombo);
  } else {
    bootCartCombo();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-combo' || event.source === 'product-form') return;

      if (document.getElementById('cart-combo-root')) {
        window.requestAnimationFrame(() =>
          window.initCartComboOfThree({ skipBundleSync: event.source === 'cart-items' })
        );
      }
    });
  }
})();
